import instance from '@src/api/axiosInstance';
import { checkIsInternalUser } from '@src/globalStore/profileStore';
import { useQuery } from '@tanstack/react-query';
import { get } from 'lodash';

interface TokenUsageData {
  pct_used: number;
  is_over: boolean;
  [key: string]: any;
}

// Color coding based on usage percentage
const getColorCode = (percentage: number) => {
  if (percentage < 70) return 'green';
  if (percentage < 90) return 'yellow';
  return 'red';
};

const useGetTokenUsage = () => {
  const isInternal = checkIsInternalUser();
  const { data, isFetching, isError, error } = useQuery<TokenUsageData>(
    ['nexa-token-usage', isInternal],
    async () => {
      const response = await instance.get('/nexa/usage/quota/status', {
        params: { is_internal: isInternal },
      });
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      notifyOnChangeProps: ['isFetching', 'isError', 'data'],
      refetchInterval: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
    },
  );

  const pctUsed = get(data, 'pct_used', 0);
  const isOver = get(data, 'is_over', false);
  const usedPercentage = Math.min(100, pctUsed * 100);
  const remainingPercentage = Math.max(0, (1 - pctUsed) * 100);

  const colorCode = getColorCode(usedPercentage);

  return {
    data,
    isFetching,
    isError,
    error,
    pctUsed,
    isOver: isInternal ? false : isOver, // internal users do not have the limit enforced
    usedPercentage: Math.round(usedPercentage),
    remainingPercentage: Math.round(remainingPercentage),
    colorCode,
  };
};

export default useGetTokenUsage;
